import { useMemo } from "react"
import { cn } from "@/lib/utils"

export interface SparklinePoint {
  t: number
  v: number | null
}

interface SparklineProps {
  data: SparklinePoint[]
  height?: number
  accent?: string
  ariaLabel: string
  className?: string
}

const VIEWBOX_WIDTH = 100

interface PathSegments {
  line: string
  area: string
}

function buildPaths(data: SparklinePoint[], height: number): PathSegments | null {
  const validValues = data.map((p) => p.v).filter((v): v is number => v !== null)
  if (validValues.length < 2) return null

  const min = Math.min(...validValues)
  const max = Math.max(...validValues)
  const range = max - min || 1
  const lastIndex = data.length - 1 || 1

  // Build the line by emitting M/L commands; null gaps break the line into
  // separate sub-paths so the chart shows actual missing data.
  const lineCommands: string[] = []
  let inSegment = false
  for (let i = 0; i < data.length; i++) {
    const point = data[i]
    if (point.v === null) {
      inSegment = false
      continue
    }
    const x = (i / lastIndex) * VIEWBOX_WIDTH
    const y = height - ((point.v - min) / range) * height
    const cmd = inSegment ? "L" : "M"
    lineCommands.push(`${cmd}${x.toFixed(2)} ${y.toFixed(2)}`)
    inSegment = true
  }
  if (lineCommands.length < 2) return null

  // Area fill: trace the line, then drop down to the baseline and back to start.
  const areaCommands = [...lineCommands]
  // Find the first and last x we drew to close the area.
  let firstX: number | null = null
  let lastX: number | null = null
  for (let i = 0; i < data.length; i++) {
    const point = data[i]
    if (point.v === null) continue
    const x = (i / lastIndex) * VIEWBOX_WIDTH
    if (firstX === null) firstX = x
    lastX = x
  }
  if (firstX !== null && lastX !== null) {
    areaCommands.push(`L${lastX.toFixed(2)} ${height}`)
    areaCommands.push(`L${firstX.toFixed(2)} ${height}`)
    areaCommands.push("Z")
  }

  return {
    line: lineCommands.join(" "),
    area: areaCommands.join(" "),
  }
}

export function Sparkline({
  data,
  height = 64,
  accent = "var(--primary)",
  ariaLabel,
  className,
}: SparklineProps) {
  const paths = useMemo(() => buildPaths(data, height), [data, height])

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className={cn("block w-full", className)}
    >
      {paths && (
        <>
          <path
            data-slot="sparkline-area"
            d={paths.area}
            fill={accent}
            fillOpacity={0.15}
            stroke="none"
          />
          <path
            data-slot="sparkline-line"
            d={paths.line}
            fill="none"
            stroke={accent}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </svg>
  )
}
